import React, { useState, useRef, useEffect, useCallback } from 'react';
import { formatDuration } from '../../utils';

interface SeekBarProps {
    currentTime: number;
    duration: number;
    onSeek: (time: number) => void;
    onScrubStart?: () => void;
    onScrubEnd?: () => void;
    buffered?: { start: number; end: number }[]; // Optional: for showing buffered ranges
}

export const SeekBar: React.FC<SeekBarProps> = ({
    currentTime,
    duration,
    onSeek,
    onScrubStart,
    onScrubEnd,
    buffered = []
}) => {
    const [isHovering, setIsHovering] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [hoverTime, setHoverTime] = useState<number | null>(null);
    const [hoverPosition, setHoverPosition] = useState<number | null>(null);
    const [dragTime, setDragTime] = useState<number | null>(null); // Optimistic time while dragging

    const containerRef = useRef<HTMLDivElement>(null);

    // Calculate progress percentage: Use dragTime if dragging, otherwise props.currentTime
    const displayTime = isDragging && dragTime !== null ? dragTime : currentTime;
    const progressPercent = duration > 0 ? (displayTime / duration) * 100 : 0;

    const getEventTime = (e: React.MouseEvent | MouseEvent) => {
        if (!containerRef.current) return 0;
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
        const percentage = x / rect.width;
        return percentage * duration;
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        onScrubStart?.();

        const newTime = getEventTime(e);
        setDragTime(newTime); // Set optimistic time immediately
        onSeek(newTime);

        // Global listeners for drag
        const handleWindowMouseMove = (moveEvent: MouseEvent) => {
            const time = getEventTime(moveEvent);
            setDragTime(time); // Update optimistic time
            onSeek(time);
        };

        const handleWindowMouseUp = () => {
            setIsDragging(false);
            setDragTime(null);
            onScrubEnd?.();
            window.removeEventListener('mousemove', handleWindowMouseMove);
            window.removeEventListener('mouseup', handleWindowMouseUp);
        };

        window.addEventListener('mousemove', handleWindowMouseMove);
        window.addEventListener('mouseup', handleWindowMouseUp);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(x / rect.width, 1));

        setHoverPosition(percentage * 100);
        setHoverTime(percentage * duration);
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
        setHoverTime(null);
        setHoverPosition(null);
    };

    return (
        <div
            ref={containerRef}
            className={`
                group absolute bottom-0 left-0 right-0 
                h-1.5 hover:h-2.5
                bg-white/10 hover:bg-black/40 backdrop-blur-md 
                cursor-pointer z-[100]
                transition-all duration-200 ease-out
                ${isDragging ? '!h-2.5' : ''}
            `}
            style={{ touchAction: 'none' }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
            onMouseDown={handleMouseDown}
        >
            {/* Invisible Hit Area Expansion (Top) - Makes it easier to grab */}
            <div className="absolute bottom-full left-0 right-0 h-4 bg-transparent w-full" />

            {/* Background Track */}
            <div className="absolute inset-x-0 bottom-0 top-0 w-full bg-white/10" />

            {/* Buffered Ranges */}
            {buffered.map((range, idx) => {
                const startPercent = (range.start / duration) * 100;
                const widthPercent = ((range.end - range.start) / duration) * 100;
                return (
                    <div
                        key={idx}
                        className="absolute top-0 bottom-0 bg-white/30"
                        style={{ left: `${startPercent}%`, width: `${widthPercent}%` }}
                    />
                );
            })}

            {/* Ghost Bar (Hover Indicator) */}
            {(isHovering || isDragging) && hoverPosition !== null && (
                <div
                    className="absolute top-0 bottom-0 bg-white/20 pointer-events-none"
                    style={{ left: 0, width: `${hoverPosition}%` }}
                />
            )}

            {/* Play Progress (Red Bar) */}
            <div
                className={`absolute top-0 bottom-0 left-0 bg-[#f00] group-hover:bg-[#f00] ${!isDragging ? 'transition-all duration-75 ease-linear' : ''}`}
                style={{ width: `${progressPercent}%` }}
            >
                {/* Scrubber Handle */}
                <div
                    className={`
                        absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 
                        w-3.5 h-3.5 rounded-full bg-[#f00] shadow-md transform 
                        scale-0 group-hover:scale-100 transition-transform duration-100
                        ${isDragging ? '!scale-100 ring-2 ring-red-500/30' : ''}
                    `}
                />
            </div>

            {/* Tooltip */}
            {(isHovering || isDragging) && (hoverPosition !== null || dragTime !== null) && (
                <div
                    className="absolute bottom-4 mb-2 px-2 py-1 bg-black/90 border border-white/10 text-white text-[11px] font-medium rounded shadow-xl pointer-events-none transform -translate-x-1/2 whitespace-nowrap z-50 backdrop-blur-sm"
                    style={{ left: `${isDragging && dragTime !== null ? (dragTime / duration) * 100 : hoverPosition}%` }}
                >
                    {formatDuration(isDragging && dragTime !== null ? dragTime : (hoverTime || 0))}
                </div>
            )}
        </div>
    );
};
